import React, { useCallback, useEffect, useState } from 'react';
import { useRaceStore } from '../../stores/raceStore';
import { Button } from '../ui/Button';
import { DateTimePicker } from '../ui/DateTimePicker';
import { SegmentsTable } from './SegmentsTable';
import { CourseMap } from './CourseMap';
import { CourseElevationProfile } from './CourseElevationProfile';
import { AddSplitDialog } from './AddSplitDialog';
import { ChartAnnotationsMenu, DEFAULT_ANNOTATIONS, AnnotationToggles } from '../ui/ChartAnnotationsMenu';
import { useTranslation } from '../../utils/i18n';
import { MapIcon, TrendingUpIcon, PlusIcon, SunriseIcon, SunsetIcon, RouteIcon, ChevronDownIcon, ChevronUpIcon, MapPinIcon, PenLineIcon, CalendarClockIcon } from 'lucide-react';
import { fetchSunTimes } from '../../utils/sunTimes';
import { WeatherForecast } from './WeatherForecast';
export function CourseTab() {
  const {
    t
  } = useTranslation();
  const {
    gpxData,
    settings,
    updateSettings
  } = useRaceStore();
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);
  const [splitDialogKm, setSplitDialogKm] = useState<number | undefined>(undefined);
  const [mapExpanded, setMapExpanded] = useState(true);
  const [weatherExpanded, setWeatherExpanded] = useState(true);
  const [elevationExpanded, setElevationExpanded] = useState(true);
  const [checkpointsExpanded, setCheckpointsExpanded] = useState(true);
  const [loadingSunTimes, setLoadingSunTimes] = useState(false);
  const [courseAnnotations, setCourseAnnotations] = useState<AnnotationToggles>(DEFAULT_ANNOTATIONS);
  const [hoverPoint, setHoverPoint] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  useEffect(() => {
    if (gpxData && gpxData.points.length > 0 && settings.startTime) {
      const fetchTimes = async () => {
        setLoadingSunTimes(true);
        const firstPoint = gpxData.points[0];
        const startDate = new Date(settings.startTime);
        const times = await fetchSunTimes(firstPoint.lat, firstPoint.lon, startDate);
        if (times) updateSettings({
          sunriseTime: times.sunrise,
          sunsetTime: times.sunset
        });
        setLoadingSunTimes(false);
      };
      fetchTimes();
    }
  }, [gpxData?.points[0]?.lat, settings.startTime?.split('T')[0]]);
  const handleElevationHover = useCallback((distKm: number | null) => {
    if (!gpxData || distKm === null) {
      setHoverPoint(null);
      return;
    }
    let closest = gpxData.points[0];
    let minDiff = Infinity;
    for (let i = 0; i < gpxData.points.length; i++) {
      const diff = Math.abs((gpxData.points[i].distFromStart || 0) - distKm);
      if (diff < minDiff) {
        minDiff = diff;
        closest = gpxData.points[i];
      }
      if (diff > minDiff + 0.5) break;
    }
    setHoverPoint({
      lat: closest.lat,
      lon: closest.lon
    });
  }, [gpxData]);
  const openSplitDialog = (km?: number) => {
    setSplitDialogKm(km);
    setIsSplitDialogOpen(true);
  };
  if (!gpxData) return null;
  return <div className="space-y-5" data-id="element-161">
      {/* Page Header */}
      <div data-id="element-162">
        <h2 className="text-2xl font-bold text-[#2D2D2D]" data-id="element-163">
          {t('course.title')}
        </h2>
        <p className="text-[#6B6B76] text-sm mt-1" data-id="element-164">{t('course.subtitle')}</p>
      </div>

      {/* Race Name & Date */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-3 sm:p-4 lg:p-5" data-id="element-165">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-166">
          <div className="flex flex-col gap-2" data-id="element-167">
            <div className="flex items-center gap-2" data-id="element-168">
              <PenLineIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#FC4C02]" data-id="element-169" />
              <span className="font-bold text-[#2D2D2D] text-sm" data-id="element-170">
                {t('course.raceName')}
              </span>
            </div>
            <input value={settings.raceName} onChange={e => updateSettings({
            raceName: e.target.value
          })} placeholder={t('course.raceNamePlaceholder')} className="w-full h-12 px-4 bg-[#F7F7FA] border border-[#E8E8ED] rounded-xl text-[#2D2D2D] text-[15px] font-medium placeholder:text-[#9B9BA7] focus:outline-none focus:ring-2 focus:ring-[#FC4C02] focus:border-transparent transition-all" data-id="element-171" />
          </div>
          <DateTimePicker label={t('course.startDateTime')} value={settings.startTime} onChange={value => updateSettings({
          startTime: value
        })} data-id="element-172" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-id="element-173">
        {/* Distance — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-174">
          <div className="w-11 h-11 rounded-xl bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-175">
            <RouteIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-176" />
          </div>
          <div data-id="element-177">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-178">
              {t('course.distance')}
            </div>
            <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-179">
              {gpxData.totalDistance.toFixed(1)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-180">
                km
              </span>
            </div>
          </div>
        </div>

        {/* Elevation+ — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-181">
          <div className="w-11 h-11 rounded-xl bg-[#00B843]/10 flex items-center justify-center flex-shrink-0" data-id="element-182">
            <TrendingUpIcon className="h-5 w-5 text-[#00B843]" data-id="element-183" />
          </div>
          <div data-id="element-184">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-185">
              {t('course.elevationPlus')}
            </div>
            <div className="text-2xl font-bold text-[#00B843] tabular-nums leading-tight" data-id="element-186">
              {Math.round(gpxData.totalElevationGain)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-187">
                m
              </span>
            </div>
          </div>
        </div>

        {/* Elevation- — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-188">
          <div className="w-11 h-11 rounded-xl bg-[#E34402]/10 flex items-center justify-center flex-shrink-0" data-id="element-189">
            <TrendingUpIcon className="h-5 w-5 text-[#E34402] rotate-180" data-id="element-190" />
          </div>
          <div data-id="element-191">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-192">
              {t('course.elevationMinus')}
            </div>
            <div className="text-2xl font-bold text-[#E34402] tabular-nums leading-tight" data-id="element-193">
              {Math.round(gpxData.totalElevationLoss)}
              <span className="text-sm font-medium text-[#9B9BA7] ml-0.5" data-id="element-194">
                m
              </span>
            </div>
          </div>
        </div>

        {/* Sunrise — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-195">
          <div className="w-11 h-11 rounded-xl bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0" data-id="element-196">
            <SunriseIcon className="h-5 w-5 text-[#FF9500]" data-id="element-197" />
          </div>
          <div data-id="element-198">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-199">
              {t('course.sunrise')}
            </div>
            <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-200">
              {loadingSunTimes ? '...' : settings.sunriseTime}
            </div>
          </div>
        </div>

        {/* Sunset — desktop only */}
        <div className="hidden lg:flex bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4 items-center gap-4" data-id="element-201">
          <div className="w-11 h-11 rounded-xl bg-[#7B61FF]/10 flex items-center justify-center flex-shrink-0" data-id="element-202">
            <SunsetIcon className="h-5 w-5 text-[#7B61FF]" data-id="element-203" />
          </div>
          <div data-id="element-204">
            <div className="text-[11px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-205">
              {t('course.sunset')}
            </div>
            <div className="text-2xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-206">
              {loadingSunTimes ? '...' : settings.sunsetTime}
            </div>
          </div>
        </div>

        {/* ALL stats combined — mobile only (single card) */}
        <div className="lg:hidden col-span-2 bg-white rounded-2xl border border-[#E8E8ED] shadow-sm p-4" data-id="element-207">
          {/* Distance */}
          <div className="flex items-center gap-3" data-id="element-208">
            <div className="w-9 h-9 rounded-lg bg-[#FC4C02]/10 flex items-center justify-center flex-shrink-0" data-id="element-209">
              <RouteIcon className="h-4 w-4 text-[#FC4C02]" data-id="element-210" />
            </div>
            <div data-id="element-211">
              <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-212">
                {t('course.distance')}
              </div>
              <div className="text-xl font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-213">
                {gpxData.totalDistance.toFixed(1)}
                <span className="text-xs font-medium text-[#9B9BA7] ml-0.5" data-id="element-214">
                  km
                </span>
              </div>
            </div>
          </div>
          {/* D+ / D- */}
          <div className="border-t border-[#F0F0F5] mt-3 pt-3 grid grid-cols-2 gap-3" data-id="element-215">
            <div className="flex items-center gap-2.5" data-id="element-216">
              <div className="w-8 h-8 rounded-lg bg-[#00B843]/10 flex items-center justify-center flex-shrink-0" data-id="element-217">
                <TrendingUpIcon className="h-3.5 w-3.5 text-[#00B843]" data-id="element-218" />
              </div>
              <div data-id="element-219">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-220">
                  D+
                </div>
                <div className="text-base font-bold text-[#00B843] tabular-nums leading-tight" data-id="element-221">
                  {Math.round(gpxData.totalElevationGain)}
                  <span className="text-[10px] font-medium text-[#9B9BA7] ml-0.5" data-id="element-222">
                    m
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5" data-id="element-223">
              <div className="w-8 h-8 rounded-lg bg-[#E34402]/10 flex items-center justify-center flex-shrink-0" data-id="element-224">
                <TrendingUpIcon className="h-3.5 w-3.5 text-[#E34402] rotate-180" data-id="element-225" />
              </div>
              <div data-id="element-226">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-227">
                  D-
                </div>
                <div className="text-base font-bold text-[#E34402] tabular-nums leading-tight" data-id="element-228">
                  {Math.round(gpxData.totalElevationLoss)}
                  <span className="text-[10px] font-medium text-[#9B9BA7] ml-0.5" data-id="element-229">
                    m
                  </span>
                </div>
              </div>
            </div>
          </div>
          {/* Sunrise / Sunset */}
          <div className="border-t border-[#F0F0F5] mt-3 pt-3 grid grid-cols-2 gap-3" data-id="element-230">
            <div className="flex items-center gap-2.5" data-id="element-231">
              <div className="w-8 h-8 rounded-lg bg-[#FF9500]/10 flex items-center justify-center flex-shrink-0" data-id="element-232">
                <SunriseIcon className="h-3.5 w-3.5 text-[#FF9500]" data-id="element-233" />
              </div>
              <div data-id="element-234">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-235">
                  {t('course.sunrise')}
                </div>
                <div className="text-base font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-236">
                  {loadingSunTimes ? '...' : settings.sunriseTime}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5" data-id="element-237">
              <div className="w-8 h-8 rounded-lg bg-[#7B61FF]/10 flex items-center justify-center flex-shrink-0" data-id="element-238">
                <SunsetIcon className="h-3.5 w-3.5 text-[#7B61FF]" data-id="element-239" />
              </div>
              <div data-id="element-240">
                <div className="text-[10px] font-semibold text-[#9B9BA7] uppercase tracking-wider" data-id="element-241">
                  {t('course.sunset')}
                </div>
                <div className="text-base font-bold text-[#2D2D2D] tabular-nums leading-tight" data-id="element-242">
                  {loadingSunTimes ? '...' : settings.sunsetTime}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Weather Forecast */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-243">
        <button onClick={() => setWeatherExpanded(!weatherExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-244">
          <span className="flex items-center gap-2.5" data-id="element-245">
            <span className="text-lg" data-id="element-246">🌤️</span>
            {t('weather.title')}
          </span>
          {weatherExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-247" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-248" />}
        </button>
        {weatherExpanded && <div className="border-t border-[#E8E8ED]" data-id="element-249">
            <WeatherForecast data-id="element-250" />
          </div>}
      </div>

      {/* Map — Collapsible, closed by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-251">
        <button onClick={() => setMapExpanded(!mapExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-252">
          <span className="flex items-center gap-2.5" data-id="element-253">
            <MapIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-254" />
            {t('course.map')}
          </span>
          {mapExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-255" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-256" />}
        </button>
        {mapExpanded && <div className="h-[300px] lg:h-[380px] border-t border-[#E8E8ED] relative" data-id="element-257">
            <CourseMap hoverPoint={hoverPoint} data-id="element-258" />
          </div>}
      </div>

      {/* Elevation Profile — Collapsible, open by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-259">
        <button onClick={() => setElevationExpanded(!elevationExpanded)} className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-260">
          <span className="flex items-center gap-2.5 flex-shrink-0" data-id="element-261">
            <TrendingUpIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-262" />
            <span className="whitespace-nowrap" data-id="element-263">
              {t('course.elevationProfile')}
            </span>
          </span>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2" data-id="element-264">
            {elevationExpanded && <>
                <span className="text-[9px] sm:text-[10px] text-[#9B9BA7] uppercase tracking-wider hidden sm:block font-semibold" data-id="element-265">
                  {t('course.clickToAdd')}
                </span>
                <ChartAnnotationsMenu annotations={courseAnnotations} onChange={setCourseAnnotations} data-id="element-266" />
              </>}
            {elevationExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-267" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-268" />}
          </div>
        </button>
        {elevationExpanded && <div className="border-t border-[#E8E8ED] p-4" data-id="element-269">
            <div className="h-[320px] lg:h-[380px]" data-id="element-270">
              <CourseElevationProfile onPointClick={openSplitDialog} onHover={handleElevationHover} annotations={courseAnnotations} data-id="element-271" />
            </div>
          </div>}
      </div>

      {/* Checkpoints — Collapsible, open by default */}
      <div className="bg-white rounded-2xl border border-[#E8E8ED] shadow-sm overflow-hidden" data-id="element-272">
        <button onClick={() => setCheckpointsExpanded(!checkpointsExpanded)} className="w-full flex items-center justify-between px-5 py-3.5 text-base font-bold text-[#2D2D2D] hover:bg-[#FAFAFA] transition-colors" data-id="element-273">
          <span className="flex items-center gap-2.5" data-id="element-274">
            <MapPinIcon className="h-5 w-5 text-[#FC4C02]" data-id="element-275" />
            {t('course.checkpoints')}
          </span>
          {checkpointsExpanded ? <ChevronUpIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-276" /> : <ChevronDownIcon className="h-5 w-5 text-[#9B9BA7]" data-id="element-277" />}
        </button>
        {checkpointsExpanded && <div className="border-t border-[#E8E8ED] p-4" data-id="element-278">
            <SegmentsTable onAdd={() => openSplitDialog()} title={t('course.checkpoints')} data-id="element-279" />
          </div>}
      </div>

      <AddSplitDialog isOpen={isSplitDialogOpen} onClose={() => setIsSplitDialogOpen(false)} initialKm={splitDialogKm} data-id="element-280" />
    </div>;
}